import React, { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { LuLogOut } from "react-icons/lu";
import { Globe, CreditCard, User, Mail, BadgeCheck, ChevronUp } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLogoutMutation } from "../../Redux/userApi";
import { useApiToast } from "../Toast/useApiToast";
import { SiCircle } from "react-icons/si";

import {
    clearUser,
    selectAvatar,
    selectDisplayName,
    selectEmail,
    selectInitials,
} from "../../Redux/authSlice";
import { useAppDispatch } from "../../Redux/hooks";
import { useUsage } from "../../context/UsageContext";
import zeroTypingLogo from "../../Asset/ZEROTyingLogo.svg";
import sideBarLogo from "../../Asset/ZT-Sidebar-logo.svg";
import arrowCollapsed from "../../Asset/ArrowCollapsed.svg";
import arrowExpanded from "../../Asset/ArrowExpanded.svg";


const topNavigation = [
    {
        name: "Web App",
        icon: <Globe size={18} />,
        link: "/app",
    },
    {
        name: "Manage Subscription",
        icon: <CreditCard size={18} />,
        link: "/settings",
    },
    {
        name: "Usage",
        icon: <SiCircle size={18} />,
        link: "/usage",
    },
];

const ProfileDetailsPopover = ({ name, email, plan, avatar, initials, onClose, anchorRef }) => {
    const popoverRef = useRef(null);

    useEffect(() => {
        const onDown = (e) => {
            if (!popoverRef.current) return;
            if (popoverRef.current.contains(e.target)) return;
            if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
            onClose();
        };
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose, anchorRef]);

    return (
        <div
            ref={popoverRef}
            role="dialog"
            aria-label="Profile details"
            className="absolute left-2 right-2 bottom-[calc(100%+8px)] z-[140] rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.12)] border border-[#E5E7EB] overflow-hidden"
        >
            <div className="flex items-center gap-3 px-4 py-3 bg-[#F7F7F8]">
                {avatar ? (
                    <img
                        src={avatar}
                        alt="Profile"
                        className="h-10 w-10 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <div className="h-10 w-10 rounded-full bg-[#EDF2F7] text-[#353535] grid place-items-center font-semibold">
                        {initials || "U"}
                    </div>
                )}
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-[#111827]">
                        {name || "Account"}
                    </div>
                </div>
            </div>

            <ul className="divide-y divide-[#F1F2F4]">
                <li className="flex items-start gap-3 px-4 py-2.5">
                    <User size={14} className="mt-0.5 text-[#9CA3AF] shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
                            Full Name
                        </div>
                        <div className="text-[13px] font-medium text-[#374151] break-words">
                            {name || "—"}
                        </div>
                    </div>
                </li>
                <li className="flex items-start gap-3 px-4 py-2.5">
                    <BadgeCheck size={14} className="mt-0.5 text-[#9CA3AF] shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
                            Usage
                        </div>
                        <div className="text-[13px] font-medium text-[#374151]">
                            {plan}
                        </div>
                    </div>
                </li>
                <li className="flex items-start gap-3 px-4 py-2.5">
                    <Mail size={14} className="mt-0.5 text-[#9CA3AF] shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
                            Email
                        </div>
                        <div className="text-[13px] font-medium text-[#374151] break-all" title={email}>
                            {email || "—"}
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    );
};

const ProfileCard = ({
    avatar,
    name,
    initials,
    plan,
    compact = false,
    isOpen = false,
    onClick,
    buttonRef,
}) => {
    if (compact) {
        return (
            <button
                ref={buttonRef}
                type="button"
                onClick={onClick}
                aria-label="Show profile details"
                aria-expanded={isOpen}
                className="flex items-center justify-center p-2 rounded-xl hover:bg-[#F3F4F6] transition-colors"
            >
                {avatar ? (
                    <img
                        src={avatar}
                        alt="Profile"
                        className="h-9 w-9 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <div className="h-9 w-9 rounded-full bg-[#EDF2F7] text-[#353535] grid place-items-center text-sm font-semibold">
                        {initials || "U"}
                    </div>
                )}
            </button>
        );
    }

    return (
        <button
            ref={buttonRef}
            type="button"
            onClick={onClick}
            aria-label="Show profile details"
            aria-expanded={isOpen}
            className="w-full text-left flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-lg md:rounded-2xl bg-[#F7F7F8] hover:bg-[#EFF0F2] transition-colors mx-1 mb-2"
        >
            {avatar ? (
                <img
                    src={avatar}
                    alt="Profile"
                    className="h-10 w-10 md:h-11 md:w-11 rounded-full object-cover shrink-0"
                    referrerPolicy="no-referrer"
                />
            ) : (
                <div className="h-10 w-10 md:h-11 md:w-11 rounded-full bg-[#EDF2F7] text-[#353535] grid place-items-center font-semibold shrink-0">
                    {initials || "U"}
                </div>
            )}

            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-[#111827]">
                    {name || "Account"}
                </div>
                <div className="text-xs text-[#9CA3AF] font-normal">
                    {plan}
                </div>
            </div>

            <ChevronUp
                size={16}
                className={`shrink-0 text-[#9CA3AF] transition-transform duration-200 ${isOpen ? "rotate-0" : "rotate-180"}`}
                aria-hidden="true"
            />
        </button>
    );
};

const Sidebar = ({
    openSidebar,
    setOpenSidebar,
    openSidebarMobile,
    setOpenSidebarMobile,
}) => {
    const { showToast } = useApiToast();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const avatar = useSelector(selectAvatar);
    const name = useSelector(selectDisplayName);
    const email = useSelector(selectEmail);
    const initials = useSelector(selectInitials);
    const { usage } = useUsage();
    const planLabel = usage?.isPro ? "Pro" : "Free";

    const [profileOpenDesktop, setProfileOpenDesktop] = useState(false);
    const [profileOpenMobile, setProfileOpenMobile] = useState(false);
    const desktopBtnRef = useRef(null);
    const mobileBtnRef = useRef(null);

    const [logout] = useLogoutMutation();
    const handleLogout = async () => {
        try {
            const response = await logout({}).unwrap();

            if (response?.success) {
                showToast(response?.message || "Logged out successfully", "success");
                dispatch(clearUser());
                navigate("/login", { replace: true });
            }
        } catch (error) {
            showToast("Logout failed", "error");
            dispatch(clearUser());
            navigate("/login", { replace: true });
        }
    };

    const userNavigation = [
        {
            name: "Sign Out",
            icon: <LuLogOut className=" size-5" />,
            buttonColor: "text-[#111827] hover:bg-[#F3F4F6]",
            onclick: () => handleLogout(),
        },
    ];


    return (
        <>
            <div
                className={`lg:block hidden fixed top-0 bottom-0 z-[120] ${!openSidebar ? " w-16" : "xl:w-[260px] w-[200px]"
                    } transition-all duration-500 ease-in-out`}
            >
                <button
                    type="button"
                    onClick={() => setOpenSidebar(!openSidebar)}
                    aria-label={openSidebar ? "Close Sidebar" : "Open Sidebar"}
                    className={`z-[130] absolute transition-all duration-300 cursor-pointer bg-[#39A7F2] hover:bg-[#2596E7] text-white border border-[#39A7F2] rounded-full p-1.5 aspect-square -right-3 top-[88px] -translate-y-1/2 shadow-sm`}
                >
                    <img
                        src={openSidebar ? arrowExpanded : arrowCollapsed}
                        alt=""
                        aria-hidden="true"
                        className="w-[12px] h-[12px] object-contain"
                    />
                </button>

                <div className="relative w-full h-screen flex flex-col justify-between overflow-y-auto scrollbar-hide py-0 border-r border-[#E5E7EB] bg-[#FFFFFF]">
                    <div className="flex flex-col">
                        <div className={`h-[88px] border-b border-[#E5E7EB] px-3 flex items-center ${!openSidebar ? "justify-center" : "justify-start"}`}>
                            <NavLink to="/" className="inline-flex items-center" aria-label="Go to website">
                                <img
                                    src={!openSidebar ? sideBarLogo : zeroTypingLogo}
                                    className={`${!openSidebar ? "w-9 h-9 object-contain" : "w-[170px] object-contain"}`}
                                    alt="Zero Typing"
                                />
                            </NavLink>
                        </div>

                        <div className="flex flex-col gap-1 px-2 py-5">
                            {topNavigation?.map((link, i) => (
                                <NavLink
                                    className={({ isActive }) =>
                                        `flex items-center ${!openSidebar ? "justify-center" : "justify-start"} gap-3 rounded-xl border transition-all ${!isActive
                                            ? "text-[#4B5563] border-transparent hover:bg-[#F3F4F6]"
                                            : "text-[#1D9BF0] bg-[#DDF0FF] border-[#92CDF9]"
                                        } text-sm ${!openSidebar ? "p-3" : "p-2 w-full"
                                        } `
                                    }
                                    key={i}
                                    to={link.link}
                                >
                                    {link.icon}
                                    <span className={`${!openSidebar ? "hidden" : "flex"} font-medium`}>
                                        {link?.name}
                                    </span>
                                </NavLink>
                            ))}
                        </div>
                    </div>


                    <div className="relative flex flex-col border-t border-[#E5E7EB] px-2 py-3">
                        {profileOpenDesktop && openSidebar && (
                            <ProfileDetailsPopover
                                name={name}
                                email={email}
                                plan={planLabel}
                                avatar={avatar}
                                initials={initials}
                                anchorRef={desktopBtnRef}
                                onClose={() => setProfileOpenDesktop(false)}
                            />
                        )}

                        {openSidebar ? (
                            <ProfileCard
                                avatar={avatar}
                                name={name}
                                initials={initials}
                                plan={planLabel}
                                isOpen={profileOpenDesktop}
                                onClick={() => setProfileOpenDesktop((v) => !v)}
                                buttonRef={desktopBtnRef}
                            />
                        ) : (
                            <ProfileCard
                                compact
                                avatar={avatar}
                                name={name}
                                initials={initials}
                                plan={planLabel}
                                onClick={() => {
                                    setOpenSidebar(true);
                                    setProfileOpenDesktop(true);
                                }}
                                buttonRef={desktopBtnRef}
                            />
                        )}

                        <div className="flex flex-col gap-1">
                            {userNavigation?.map((link, i) => {
                                if (link.link) {
                                    return (
                                        <NavLink
                                            key={i}
                                            to={link.link}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 rounded-lg transition-all ${link?.buttonColor ? link.buttonColor :
                                                    (!isActive ? "text-[#4B5563] hover:bg-[#F3F4F6]"
                                                        : "text-[#1D9BF0] bg-[#DDF0FF] border border-[#92CDF9]")
                                                } text-sm ${!openSidebar ? "p-3 justify-center" : "px-3 py-3 w-full"}`
                                            }
                                        >
                                            {link.icon}
                                            <span className={`${!openSidebar ? "hidden" : "flex"} font-medium`}>
                                                {link.name}
                                            </span>
                                        </NavLink>
                                    );
                                } else {
                                    return (
                                        <button
                                            key={i}
                                            onClick={link.onclick}
                                            type="button"
                                            className={`flex items-center gap-3 rounded-lg transition-all ${link?.buttonColor ? link.buttonColor : "text-[#4B5563] hover:bg-[#F3F4F6]"
                                                } text-sm ${!openSidebar ? "p-3 justify-center" : "px-3 py-3 w-full"}`}
                                        >
                                            {link.icon}
                                            <span className={`${!openSidebar ? "hidden" : "flex"} font-medium`}>
                                                {link.name}
                                            </span>
                                        </button>
                                    );
                                }
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <div
                className={`fixed lg:hidden inset-0 bg-black/25 transition-opacity duration-300 z-[160] ${openSidebarMobile ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
                onClick={() => setOpenSidebarMobile(false)}
            />
            <div
                className={`fixed lg:hidden flex flex-col w-[75%] inset-y-0 left-0 bg-white transition-transform duration-700 ease-in-out h-[100dvh] z-[170] ${openSidebarMobile ? "translate-x-0" : "-translate-x-[105%]"
                    }`}
            >
                <div className="w-full px-4 h-[72px] flex justify-start border-b border-[#E5E7EB] items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setOpenSidebarMobile(false)}
                        className="outline-0 text-[#374151]"
                    >
                        <FiX className=" size-6" />
                    </button>
                    <NavLink to="/" onClick={() => setOpenSidebarMobile(false)} aria-label="Go to website">
                        <img src={zeroTypingLogo} className="w-[140px] object-contain" alt="logo" />
                    </NavLink>

                </div>
                <div className="relative w-full h-[calc(100dvh-72px)] max-h-[calc(100dvh-72px)] flex flex-col gap-3 overflow-y-auto scrollbar-hide py-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
                    <div className="flex flex-col pb-3 px-3">
                        <div className="flex flex-col gap-1">
                            {topNavigation?.map((link, i) => (
                                <NavLink
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 rounded-xl border transition-all ${!isActive
                                            ? "text-[#4B5563] border-transparent hover:bg-[#F3F4F6]"
                                            : "text-[#1D9BF0] bg-[#DDF0FF] border-[#92CDF9]"
                                        } text-sm p-2 w-full `
                                    }
                                    key={i}
                                    to={link.link}
                                    onClick={() => setOpenSidebarMobile(false)}
                                >
                                    {link.icon}
                                    <span className="font-medium">{link?.name}</span>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                    <div className="relative mt-auto flex flex-col gap-3 border-t border-[#E5E7EB] pt-3 px-3">
                        {profileOpenMobile && (
                            <ProfileDetailsPopover
                                name={name}
                                email={email}
                                plan={planLabel}
                                avatar={avatar}
                                initials={initials}
                                anchorRef={mobileBtnRef}
                                onClose={() => setProfileOpenMobile(false)}
                            />
                        )}

                        <ProfileCard
                            avatar={avatar}
                            name={name}
                            initials={initials}
                            plan={planLabel}
                            isOpen={profileOpenMobile}
                            onClick={() => setProfileOpenMobile((v) => !v)}
                            buttonRef={mobileBtnRef}
                        />

                        <div className="flex flex-col gap-1">
                            {userNavigation?.map((link, i) => {
                                if (link.link) {
                                    return (
                                        <NavLink
                                            key={i}
                                            to={link.link}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 rounded-lg transition-all ${link?.buttonColor ? link.buttonColor :
                                                    (!isActive ? "text-[#4B5563] hover:bg-[#F3F4F6]"
                                                        : "text-[#1D9BF0] bg-[#DDF0FF] border border-[#92CDF9]")
                                                } text-sm px-4 py-3 w-full`
                                            }
                                        >
                                            {link?.icon} <span className="font-medium">{link?.name}</span>
                                        </NavLink>
                                    );
                                } else {
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => {
                                                if (link.onclick) link.onclick();
                                                setOpenSidebarMobile(false);
                                            }}
                                            className={`flex items-center gap-3 rounded-lg transition-all ${link?.buttonColor ? link.buttonColor : "text-[#4B5563] hover:bg-[#F3F4F6]"
                                                } text-sm px-4 py-3 w-full`}
                                        >
                                            {link?.icon} <span className="font-medium">{link?.name}</span>
                                        </button>
                                    );
                                }
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
